// Namespace import + unwrap: lambda/ has no "type": "module", so under tsx (the Typesense
// migration CLI and other scripts load this via build-typesense-search-params) a named import
// throws at module load. See server/utils/lambda-interop.ts.
import * as localeEnumFieldsNs from '../../lambda/utils/processor/locale-enum-fields';
import { unwrapLambdaModule } from '../../server/utils/lambda-interop';
import { REACH_REGION_TAGS } from './reach-region-tags';

const { EDUCATION_LEVEL_EN, EDUCATION_LEVEL_FI, EDUCATION_LEVEL_SV, JOB_TYPE_EN, JOB_TYPE_FI, JOB_TYPE_SV } =
  unwrapLambdaModule(localeEnumFieldsNs);

/**
 * Default order of the remote jobs hub (`/jobs/remote`, `/tyopaikat/etatyo`, `/lediga-jobb/distans`),
 * computed at query time from fields every job document already has: no stored tier, no
 * migration, no re-index. Nothing is hidden; only page 1 changes.
 *
 * Typesense `_eval([ (filter):weight, ... ])` gives each document the weight of the FIRST
 * expression it matches (0 when none match), so the list below reads as a ladder, top rule wins.
 *
 * The rules trust the job data. Jobs whose eligibility or remoteness is wrong (a "Remote - US"
 * role tagged worldwide, a construction job tagged remote) are fixed in extraction, not demoted
 * here.
 */

export type RemoteHubSortLocale = 'en' | 'fi' | 'sv';

/** AI-training gig marketplaces: listed, but after everything else. */
export const REMOTE_HUB_GIG_COMPANY_IDS: readonly string[] = ['Welocalize', 'Mindrift', 'Invisible Agency'];

/** Rules for a job only count while it is fresher than this; older jobs fall to the default band. */
export const REMOTE_HUB_FRESH_DAYS = 120;

/** Minimum monthly salary (EUR) that counts as a real, stated salary. */
export const REMOTE_HUB_SALARY_FLOOR = 1800;

const DAY_MS = 24 * 60 * 60 * 1000;

/** `requiredLanguages` values as each locale's collection stores them. */
const LANGUAGE: Record<RemoteHubSortLocale, { english: string; finnish: string; swedish: string }> = {
  en: { english: 'English', finnish: 'Finnish', swedish: 'Swedish' },
  fi: { english: 'Englanti', finnish: 'Suomi', swedish: 'Ruotsi' },
  sv: { english: 'Engelska', finnish: 'Finska', swedish: 'Svenska' },
};

const JOB_TYPES: Record<RemoteHubSortLocale, readonly string[]> = { en: JOB_TYPE_EN, fi: JOB_TYPE_FI, sv: JOB_TYPE_SV };
const EDUCATION_LEVELS: Record<RemoteHubSortLocale, readonly string[]> = { en: EDUCATION_LEVEL_EN, fi: EDUCATION_LEVEL_FI, sv: EDUCATION_LEVEL_SV };

function tick(value: string): string {
  return `\`${value.replace(/`/g, '\\`')}\``;
}

/** The locale's value for a canonical English enum value (the en/fi/sv lists are index-aligned). */
function localized(lists: Record<RemoteHubSortLocale, readonly string[]>, locale: RemoteHubSortLocale, english: string): string {
  return lists[locale][lists.en.indexOf(english)];
}

/** Start of the UTC day `REMOTE_HUB_FRESH_DAYS` ago, so the sort string (and its cache key) only changes once a day. */
export function remoteHubFreshCutoff(now: number): number {
  return Math.floor(now / DAY_MS) * DAY_MS - REMOTE_HUB_FRESH_DAYS * DAY_MS;
}

/** The weighted `_eval` rules for one locale, highest band first. Exported for tests. */
export function buildRemoteHubSortRules(locale: RemoteHubSortLocale, now: number = Date.now()): Array<[string, number]> {
  const lang = LANGUAGE[locale];
  const has = (value: string) => `requiredLanguages:=${tick(value)}`;
  const lacks = (value: string) => `requiredLanguages:!=${tick(value)}`;
  const salary = `salaryRangeMin:>${REMOTE_HUB_SALARY_FLOOR} && salaryIsEstimate:!=true`;
  const fresh = `postedDate:>=${remoteHubFreshCutoff(now)}`;
  const noDegree = `educationLevel:=${tick(localized(EDUCATION_LEVELS, locale, 'No degree required'))}`;
  const inFinland = `regionTags:=${tick(REACH_REGION_TAGS.finland)}`;
  const finlandOrEurope = `regionTags:=[${tick(REACH_REGION_TAGS.finland)},${tick(REACH_REGION_TAGS.europe)}]`;
  const backOfList = [
    `jobType:=[${['Open Application', 'Contract'].map((t) => tick(localized(JOB_TYPES, locale, t))).join(',')}]`,
    'isOpenApplication:true',
    `companyId:=[${REMOTE_HUB_GIG_COMPANY_IDS.map(tick).join(',')}]`,
  ].join(' || ');

  const rules: Array<[string, number]> = [[backOfList, -1]];
  if (locale === 'fi') {
    rules.push(
      [`${has(lang.finnish)} && ${salary} && ${fresh}`, 4],
      [`${has(lang.finnish)} && ${fresh}`, 3],
      [`(${noDegree} || ${inFinland}) && ${fresh}`, 2],
    );
  } else if (locale === 'en') {
    const englishOnly = `${has(lang.english)} && ${lacks(lang.finnish)}`;
    rules.push(
      [`${englishOnly} && ${finlandOrEurope} && ${salary} && ${fresh}`, 4],
      [`${englishOnly} && ${finlandOrEurope} && ${fresh}`, 3],
      [`${englishOnly} && ${salary} && ${fresh}`, 2],
    );
  } else {
    rules.push(
      [`${has(lang.swedish)} && ${salary} && ${fresh}`, 5],
      [`${has(lang.swedish)} && ${fresh}`, 4],
      [`${has(lang.finnish)} && ${salary} && ${fresh}`, 3],
      [`${has(lang.finnish)} && ${fresh}`, 2],
    );
  }
  return rules;
}

/** `sort_by` for a remote-only hub search with no text query in `locale`'s collection. */
export function buildRemoteHubSortBy(locale: RemoteHubSortLocale, now: number = Date.now()): string {
  const rules = buildRemoteHubSortRules(locale, now)
    .map(([filter, weight]) => `(${filter}):${weight}`)
    .join(', ');
  return `_eval([ ${rules} ]):desc,score:desc`;
}
